import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import Vapor from "laravel-vapor";
import environment from "constants/apiConst";
import { AddPhotography } from "pages/Pass-Form/View/Adjunto/AddPhotography";
import { hasCommonElements } from "utils/type-check-utils";
import {
  __saveSecurityPhotography,
  __getSecurityPhotography,
  __deleteSecurityPhotography
} from "request/__Security";

/**
 * EF4-1575: Sección autocontenida de "Fotos de seguridad" para el pase de
 * entrada / salida, separada visualmente de las fotos de comercio exterior.
 *
 * - Solo el personal de SEGURIDAD (permiso saveSecurityPhotography*) puede
 *   subir/eliminar; el resto de roles la ve en solo lectura.
 * - Reutiliza <AddPhotography/> (adjuntar archivo o abrir la cámara del
 *   dispositivo mediante capture="environment").
 *
 * Props:
 *  - passId: id del pase de entrada (destiny="entry") o de salida (destiny="exit").
 *  - destiny: "entry" | "exit".
 *  - initialPhotos: (opcional) arreglo security_photographies que ya viene del
 *    detalle (showEntryPass/showDispatchPass). Si no se pasa, se consulta al backend.
 *  - title: (opcional) título de la sección.
 */
const SecurityPhotographySection = ({
  passId,
  destiny = "entry",
  initialPhotos = null,
  title = "Fotos de seguridad"
}) => {
  const token = useSelector((state) => state.auth?.dataUser?.token);
  const { permissionList } = useSelector((state) => state.permissions);

  const [securityPhotos, setSecurityPhotos] = useState(
    Array.isArray(initialPhotos) ? initialPhotos : []
  );

  // Solo seguridad (o quien tenga el permiso de subir fotos de seguridad) puede gestionar.
  const canManage = useMemo(() => {
    const requiredPermission =
      destiny === "entry"
        ? "saveSecurityPhotographyEntry"
        : "saveSecurityPhotographyOutput";
    return hasCommonElements(permissionList, [requiredPermission]);
  }, [permissionList, destiny]);

  const fetchPhotos = useCallback(async () => {
    if (!passId) return;
    try {
      const res = await __getSecurityPhotography(token, passId, destiny);
      const list = res?.data?.status?.data;
      if (Array.isArray(list)) setSecurityPhotos(list);
    } catch (error) {
      console.error("Error obteniendo fotos de seguridad:", error);
    }
  }, [passId, token, destiny]);

  // Si el detalle ya envía las fotos, se sincronizan; si no, se consultan.
  useEffect(() => {
    if (Array.isArray(initialPhotos)) {
      setSecurityPhotos(initialPhotos);
    }
  }, [initialPhotos]);

  useEffect(() => {
    if (!Array.isArray(initialPhotos) && passId) {
      fetchPhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passId]);

  const uploadFile = useCallback(
    (file) =>
      Vapor.store(file, {
        baseURL: environment.VAPOR_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`
        }
      }),
    [token]
  );

  // Firma compatible con <AddPhotography/>: (event, file, progressCallback)
  const updateAndCreate = useCallback(
    async (event, file, progressCallback) => {
      try {
        if (event?.preventDefault) event.preventDefault();
        const realFile = file || event?.target?.files?.[0];
        if (!realFile || !passId) return;

        const uploadResponse = await uploadFile(realFile);

        const formData = new FormData();
        formData.append("id", passId);
        formData.append(
          "photography[]",
          JSON.stringify({ file: uploadResponse, name: realFile.name })
        );

        await __saveSecurityPhotography(token, formData, destiny);
        await fetchPhotos();

        if (progressCallback) progressCallback(100);
      } catch (error) {
        console.error("Error subiendo foto de seguridad:", error);
      }
    },
    [passId, token, destiny, uploadFile, fetchPhotos]
  );

  const deleteHandler = useCallback(
    async (item) => {
      const res = await __deleteSecurityPhotography(token, item.id, destiny);
      await fetchPhotos();
      return res;
    },
    [token, destiny, fetchPhotos]
  );

  if (!passId) return null;

  return (
    <AddPhotography
      title={title}
      photography={securityPhotos}
      setPhotography={setSecurityPhotos}
      updateAndCreate={updateAndCreate}
      deleteHandler={deleteHandler}
      canManage={canManage}
    />
  );
};

export default SecurityPhotographySection;
